import React, { useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import Main from "../../../components/layout/Main";
import AlertDialog from "../../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../../components/common/ToggleTop";
import useAlive from "../../../hooks/useAlive";
import { MODELS, isModel } from "../../../constants/devices";
import { BusyOverlay, DEFAULT_ERROR, goBack } from "../settings/shared";
import {
  KINDS,
  NAME_MAX,
  SETTLE_MS,
  WAIT,
  ZONES,
  batteryOf,
  deleteCommand,
  imageStyle,
  isEnabled,
  nameOf,
  remoteCommand,
  reportChange,
  sendCommand,
  sensorCommand,
  sensorState,
  typeOfAccessory,
  wait,
  zoneOf,
} from "./accessories";
import "../../../assets/styles/app-security-accessories.css";

const NAME_ID = "hx-acc-name-field";
const ZONE_ID = "hx-acc-zone-field";

// One accessory of a WZLTEG_1 gateway (app/screens/SecurityDeviceDetails.js):
// its picture, a sensor's mode, state and battery, the name and a sensor's
// zone with Save, and Delete Device. After a save or delete the app keeps its
// loader up for 2 s, then closes; this page then goes back to the list.
// Location state: { data: the gateway, accessory: its record from the list, kind: "sensor" | "remote" }.
function AccessoryDetail() {
  const location = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = location.state?.data;
  const accessory = location.state?.accessory;
  const kind = location.state?.kind;
  const isSensor = kind === KINDS.SENSOR;
  const valid = Boolean(accessory) && isModel(device, MODELS.WZLTEG_1) && (isSensor || kind === KINDS.REMOTE);
  const savedName = nameOf(accessory, kind);
  // The zone as the menu shows it; the app shows Normal for an unknown zone.
  const shownZone = zoneOf(accessory);
  const [name, setName] = useState(savedName);
  const [zone, setZone] = useState(shownZone);
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(false);

  if (!valid) return <Redirect to="/dashboard" />;

  const type = typeOfAccessory(accessory, kind);
  const openClose = isSensor ? sensorState(accessory) : null;
  const changed = name !== savedName || (isSensor && zone !== shownZone);

  // The app's submitAPI: a sensor's whole record with the new name and zone,
  // or a remote's id and name.
  const save = async (event) => {
    event.preventDefault();
    if (busy || !changed) return;
    const updated = isSensor
      ? { ...accessory, acc_name: name, acc_zone: zone === shownZone ? accessory.acc_zone || "0" : zone }
      : { ...accessory, rm_name: name };
    setBusy(WAIT);
    try {
      await sendCommand(device, isSensor ? sensorCommand(updated) : remoteCommand(updated));
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
      return;
    }
    reportChange({ applianceId: device.applianceId, kind, action: "saved", accessory: updated });
    await wait(SETTLE_MS);
    if (!alive.current) return;
    setBusy(null);
    toast("Updated successfully!", "green");
    goBack(history);
  };

  // The app's deleteAPI, after its confirmation.
  const remove = async () => {
    setConfirming(false);
    if (busy) return;
    setBusy(WAIT);
    try {
      await sendCommand(device, deleteCommand(kind, accessory));
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
      return;
    }
    reportChange({ applianceId: device.applianceId, kind, action: "deleted", accessory });
    await wait(SETTLE_MS);
    if (!alive.current) return;
    setBusy(null);
    toast("Device deleted!", "green");
    goBack(history);
  };

  return (
    <Main header="stack" title={type.label} hideTabBar>
      <form className="hx-acc hx-acc-detail" onSubmit={save} noValidate>
        <div className="hx-acc-hero">
          <span className="hx-acc-img is-large" style={imageStyle(type.image)} aria-hidden="true" />
          {isSensor ? (
            <dl className="hx-acc-status">
              <div>
                <dt>Mode</dt>
                <dd>{isEnabled(accessory) ? "Active" : "Inactive"}</dd>
              </div>
              {openClose ? (
                <div>
                  <dt>Status</dt>
                  <dd>{openClose}</dd>
                </div>
              ) : null}
              <div>
                <dt>Battery</dt>
                <dd>{batteryOf(accessory)}</dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="hx-acc-row">
          <label className="hx-acc-label" htmlFor={NAME_ID}>
            {isSensor ? "Accessory Name" : "Remote Name"}
          </label>
          <input
            id={NAME_ID}
            className="hx-acc-input"
            type="text"
            value={name}
            maxLength={NAME_MAX}
            placeholder={isSensor ? "Zone Name" : "Remote Name"}
            autoComplete="off"
            aria-describedby={`${NAME_ID}-hint`}
            onChange={(event) => setName(event.target.value)}
          />
          <span id={`${NAME_ID}-hint`} className="hx-acc-sr">
            Up to {NAME_MAX} characters.
          </span>
        </div>

        {isSensor ? (
          <div className="hx-acc-row">
            <label className="hx-acc-label" htmlFor={ZONE_ID}>
              Accessory Zone
            </label>
            <span className="hx-acc-select">
              <select id={ZONE_ID} value={zone} onChange={(event) => setZone(event.target.value)}>
                {ZONES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} Zone
                  </option>
                ))}
              </select>
              <IoChevronDown aria-hidden="true" />
            </span>
          </div>
        ) : null}

        <div className="hx-acc-actions">
          <button type="submit" className="hx-acc-save" disabled={!changed}>
            Save
          </button>
          <button
            type="button"
            className="hx-acc-delete"
            aria-haspopup="dialog"
            onClick={() => {
              if (!busy) setConfirming(true);
            }}
          >
            Delete Device
          </button>
        </div>
      </form>

      <BusyOverlay message={busy} />
      <AlertDialog
        open={confirming}
        type="confirmation"
        title="Delete Device"
        message={`Are you sure you want to delete this Device "${savedName || type.label}"?`}
        onClose={() => setConfirming(false)}
        onConfirm={remove}
      />
    </Main>
  );
}

export default AccessoryDetail;
